import React from 'react';
import { AdminSettings } from '../../types';
import { AdminField, PolicyToggle } from './Fields';

type PolicyTabProps = {
  settings: AdminSettings;
  setSettings: (s: AdminSettings) => void;
  saveSettings: (s: AdminSettings) => void;
};

export const PolicyTab: React.FC<PolicyTabProps> = ({ settings, setSettings, saveSettings }) => (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 animate-in slide-in-from-bottom-4">
    <section className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm p-10">
      <h3 className="text-sm font-black text-slate-800 uppercase tracking-[0.2em] mb-10 border-b border-slate-50 pb-6">Global Ops Policy</h3>
      <div className="grid grid-cols-1 gap-8">
        <AdminField label="Billing Logic" value={settings.billingTimeBasis} type="select" options={['BLOCK_TIME', 'ESTIMATED']} onChange={v => {
          const newSettings = { ...settings, billingTimeBasis: v as any };
          setSettings(newSettings);
          setTimeout(() => saveSettings(newSettings), 1000);
        }} />
        <AdminField label="Base Station" value={settings.returnBaseAirstrip} type="text" onChange={v => {
          const newSettings = { ...settings, returnBaseAirstrip: v };
          setSettings(newSettings);
          setTimeout(() => saveSettings(newSettings), 1000);
        }} />
        <AdminField label="FX (TZS/USD)" value={settings.fxRateUSD_TZS.toString()} type="number" onChange={v => {
          const newSettings = { ...settings, fxRateUSD_TZS: parseInt(v) };
          setSettings(newSettings);
          setTimeout(() => saveSettings(newSettings), 1000);
        }} />
      </div>
    </section>

    <section className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm p-10">
      <h3 className="text-sm font-black text-slate-800 uppercase tracking-[0.2em] mb-10 border-b border-slate-50 pb-6">Wait Time Matrix</h3>
      <div className="grid grid-cols-1 gap-8">
        <AdminField label="Free Wait Minutes" value={settings.waitFreeMinutes.toString()} type="number" onChange={v => {
          const parsed = parseInt(v, 10);
          const newSettings = { ...settings, waitFreeMinutes: Number.isFinite(parsed) ? parsed : settings.waitFreeMinutes };
          setSettings(newSettings);
          setTimeout(() => saveSettings(newSettings), 1000);
        }} />
        <AdminField label="Hourly Wait Rate ($)" value={settings.waitRatePerHour.toString()} type="number" onChange={v => {
          const parsed = parseInt(v, 10);
          const newSettings = { ...settings, waitRatePerHour: Number.isFinite(parsed) ? parsed : settings.waitRatePerHour };
          setSettings(newSettings);
          setTimeout(() => saveSettings(newSettings), 1000);
        }} />
        <AdminField label="Escalation Threshold (H)" value={settings.waitEscalationHours.toString()} type="number" onChange={v => {
          const parsed = parseInt(v, 10);
          const newSettings = { ...settings, waitEscalationHours: Number.isFinite(parsed) ? parsed : settings.waitEscalationHours };
          setSettings(newSettings);
          setTimeout(() => saveSettings(newSettings), 1000);
        }} />
        <AdminField label="Sleep Over Tier 1 ($)" value={String(settings.sleepOverPolicy?.tier1Price ?? 0)} type="number" onChange={v => {
          const parsed = parseFloat(v);
          const newSettings = {
            ...settings,
            sleepOverPolicy: {
              ...(settings.sleepOverPolicy || { tier1Price: 0, tier2Price: 0 }),
              tier1Price: Number.isFinite(parsed) ? Math.max(0, parsed) : (settings.sleepOverPolicy?.tier1Price ?? 0),
            },
          };
          setSettings(newSettings);
          setTimeout(() => saveSettings(newSettings), 1000);
        }} />
        <AdminField label="Sleep Over Tier 2 ($)" value={String(settings.sleepOverPolicy?.tier2Price ?? 0)} type="number" onChange={v => {
          const parsed = parseFloat(v);
          const newSettings = {
            ...settings,
            sleepOverPolicy: {
              ...(settings.sleepOverPolicy || { tier1Price: 0, tier2Price: 0 }),
              tier2Price: Number.isFinite(parsed) ? Math.max(0, parsed) : (settings.sleepOverPolicy?.tier2Price ?? 0),
            },
          };
          setSettings(newSettings);
          setTimeout(() => saveSettings(newSettings), 1000);
        }} />
      </div>
    </section>

    <section className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm p-10">
      <h3 className="text-sm font-black text-slate-800 uppercase tracking-[0.2em] mb-10 border-b border-slate-50 pb-6">Charter Cancellation Policy</h3>
      <div className="grid grid-cols-1 gap-8">
        <AdminField label="Free Cancel Window (H)" value={settings.cancellationPolicies.charter.freeCancelHours.toString()} type="number" onChange={v => {
          const newSettings = {
            ...settings,
            cancellationPolicies: {
              ...settings.cancellationPolicies,
              charter: { ...settings.cancellationPolicies.charter, freeCancelHours: parseInt(v) },
            },
          };
          setSettings(newSettings);
          setTimeout(() => saveSettings(newSettings), 1000);
        }} />
        <div className="space-y-3">
          <PolicyToggle label="Enable Cancel Fee < 24H" checked={settings.cancellationPolicies.charter.enableCancelFee24h} onChange={checked => {
            const newSettings = {
              ...settings,
              cancellationPolicies: {
                ...settings.cancellationPolicies,
                charter: { ...settings.cancellationPolicies.charter, enableCancelFee24h: checked },
              },
            };
            setSettings(newSettings);
            setTimeout(() => saveSettings(newSettings), 1000);
          }} />
          <AdminField label="Cancel Fee < 24H (%)" value={settings.cancellationPolicies.charter.cancelFee24hPercent.toString()} type="number" disabled={!settings.cancellationPolicies.charter.enableCancelFee24h} onChange={v => {
            const newSettings = {
              ...settings,
              cancellationPolicies: {
                ...settings.cancellationPolicies,
                charter: { ...settings.cancellationPolicies.charter, cancelFee24hPercent: parseInt(v) },
              },
            };
            setSettings(newSettings);
            setTimeout(() => saveSettings(newSettings), 1000);
          }} />
        </div>
        <div className="space-y-3">
          <PolicyToggle label="Enable Cancel Fee < 6H" checked={settings.cancellationPolicies.charter.enableCancelFee6h} onChange={checked => {
            const newSettings = {
              ...settings,
              cancellationPolicies: {
                ...settings.cancellationPolicies,
                charter: { ...settings.cancellationPolicies.charter, enableCancelFee6h: checked },
              },
            };
            setSettings(newSettings);
            setTimeout(() => saveSettings(newSettings), 1000);
          }} />
          <AdminField label="Cancel Fee < 6H (%)" value={settings.cancellationPolicies.charter.cancelFee6hPercent.toString()} type="number" disabled={!settings.cancellationPolicies.charter.enableCancelFee6h} onChange={v => {
            const newSettings = {
              ...settings,
              cancellationPolicies: {
                ...settings.cancellationPolicies,
                charter: { ...settings.cancellationPolicies.charter, cancelFee6hPercent: parseInt(v) },
              },
            };
            setSettings(newSettings);
            setTimeout(() => saveSettings(newSettings), 1000);
          }} />
        </div>
        <div className="space-y-3">
          <PolicyToggle label="Enable No-Show Fee" checked={settings.cancellationPolicies.charter.enableNoShowFee} onChange={checked => {
            const newSettings = {
              ...settings,
              cancellationPolicies: {
                ...settings.cancellationPolicies,
                charter: { ...settings.cancellationPolicies.charter, enableNoShowFee: checked },
              },
            };
            setSettings(newSettings);
            setTimeout(() => saveSettings(newSettings), 1000);
          }} />
          <AdminField label="No-Show Fee (%)" value={settings.cancellationPolicies.charter.noShowFeePercent.toString()} type="number" disabled={!settings.cancellationPolicies.charter.enableNoShowFee} onChange={v => {
            const newSettings = {
              ...settings,
              cancellationPolicies: {
                ...settings.cancellationPolicies,
                charter: { ...settings.cancellationPolicies.charter, noShowFeePercent: parseInt(v) },
              },
            };
            setSettings(newSettings);
            setTimeout(() => saveSettings(newSettings), 1000);
          }} />
        </div>
        <div className="space-y-3">
          <PolicyToggle label="Enable Amendment Fee" checked={settings.cancellationPolicies.charter.enableAmendmentFee} onChange={checked => {
            const newSettings = {
              ...settings,
              cancellationPolicies: {
                ...settings.cancellationPolicies,
                charter: { ...settings.cancellationPolicies.charter, enableAmendmentFee: checked },
              },
            };
            setSettings(newSettings);
            setTimeout(() => saveSettings(newSettings), 1000);
          }} />
          <AdminField label="Amendment Fee (%)" value={settings.cancellationPolicies.charter.amendmentFeePercent.toString()} type="number" disabled={!settings.cancellationPolicies.charter.enableAmendmentFee} onChange={v => {
            const newSettings = {
              ...settings,
              cancellationPolicies: {
                ...settings.cancellationPolicies,
                charter: { ...settings.cancellationPolicies.charter, amendmentFeePercent: parseInt(v) },
              },
            };
            setSettings(newSettings);
            setTimeout(() => saveSettings(newSettings), 1000);
          }} />
        </div>
        <div className="space-y-3">
          <PolicyToggle label="Enable Name Change Fee" checked={settings.cancellationPolicies.charter.enableNameChangeFee} onChange={checked => {
            const newSettings = {
              ...settings,
              cancellationPolicies: {
                ...settings.cancellationPolicies,
                charter: { ...settings.cancellationPolicies.charter, enableNameChangeFee: checked },
              },
            };
            setSettings(newSettings);
            setTimeout(() => saveSettings(newSettings), 1000);
          }} />
          <AdminField label="Name Change Fee (%)" value={settings.cancellationPolicies.charter.nameChangeFeePercent.toString()} type="number" disabled={!settings.cancellationPolicies.charter.enableNameChangeFee} onChange={v => {
            const newSettings = {
              ...settings,
              cancellationPolicies: {
                ...settings.cancellationPolicies,
                charter: { ...settings.cancellationPolicies.charter, nameChangeFeePercent: parseInt(v) },
              },
            };
            setSettings(newSettings);
            setTimeout(() => saveSettings(newSettings), 1000);
          }} />
        </div>
      </div>
    </section>

    <section className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm p-10">
      <h3 className="text-sm font-black text-slate-800 uppercase tracking-[0.2em] mb-10 border-b border-slate-50 pb-6">Scheduled Cancellation Policy</h3>
      <div className="grid grid-cols-1 gap-8">
        <AdminField label="Free Cancel Window (H)" value={settings.cancellationPolicies.scheduled.freeCancelHours.toString()} type="number" onChange={v => {
          const newSettings = {
            ...settings,
            cancellationPolicies: {
              ...settings.cancellationPolicies,
              scheduled: { ...settings.cancellationPolicies.scheduled, freeCancelHours: parseInt(v) },
            },
          };
          setSettings(newSettings);
          setTimeout(() => saveSettings(newSettings), 1000);
        }} />
        <div className="space-y-3">
          <PolicyToggle label="Enable Cancel Fee < 24H" checked={settings.cancellationPolicies.scheduled.enableCancelFee24h} onChange={checked => {
            const newSettings = {
              ...settings,
              cancellationPolicies: {
                ...settings.cancellationPolicies,
                scheduled: { ...settings.cancellationPolicies.scheduled, enableCancelFee24h: checked },
              },
            };
            setSettings(newSettings);
            setTimeout(() => saveSettings(newSettings), 1000);
          }} />
          <AdminField label="Cancel Fee < 24H (%)" value={settings.cancellationPolicies.scheduled.cancelFee24hPercent.toString()} type="number" disabled={!settings.cancellationPolicies.scheduled.enableCancelFee24h} onChange={v => {
            const newSettings = {
              ...settings,
              cancellationPolicies: {
                ...settings.cancellationPolicies,
                scheduled: { ...settings.cancellationPolicies.scheduled, cancelFee24hPercent: parseInt(v) },
              },
            };
            setSettings(newSettings);
            setTimeout(() => saveSettings(newSettings), 1000);
          }} />
        </div>
        <div className="space-y-3">
          <PolicyToggle label="Enable Cancel Fee < 6H" checked={settings.cancellationPolicies.scheduled.enableCancelFee6h} onChange={checked => {
            const newSettings = {
              ...settings,
              cancellationPolicies: {
                ...settings.cancellationPolicies,
                scheduled: { ...settings.cancellationPolicies.scheduled, enableCancelFee6h: checked },
              },
            };
            setSettings(newSettings);
            setTimeout(() => saveSettings(newSettings), 1000);
          }} />
          <AdminField label="Cancel Fee < 6H (%)" value={settings.cancellationPolicies.scheduled.cancelFee6hPercent.toString()} type="number" disabled={!settings.cancellationPolicies.scheduled.enableCancelFee6h} onChange={v => {
            const newSettings = {
              ...settings,
              cancellationPolicies: {
                ...settings.cancellationPolicies,
                scheduled: { ...settings.cancellationPolicies.scheduled, cancelFee6hPercent: parseInt(v) },
              },
            };
            setSettings(newSettings);
            setTimeout(() => saveSettings(newSettings), 1000);
          }} />
        </div>
        <div className="space-y-3">
          <PolicyToggle label="Enable No-Show Fee" checked={settings.cancellationPolicies.scheduled.enableNoShowFee} onChange={checked => {
            const newSettings = {
              ...settings,
              cancellationPolicies: {
                ...settings.cancellationPolicies,
                scheduled: { ...settings.cancellationPolicies.scheduled, enableNoShowFee: checked },
              },
            };
            setSettings(newSettings);
            setTimeout(() => saveSettings(newSettings), 1000);
          }} />
          <AdminField label="No-Show Fee (%)" value={settings.cancellationPolicies.scheduled.noShowFeePercent.toString()} type="number" disabled={!settings.cancellationPolicies.scheduled.enableNoShowFee} onChange={v => {
            const newSettings = {
              ...settings,
              cancellationPolicies: {
                ...settings.cancellationPolicies,
                scheduled: { ...settings.cancellationPolicies.scheduled, noShowFeePercent: parseInt(v) },
              },
            };
            setSettings(newSettings);
            setTimeout(() => saveSettings(newSettings), 1000);
          }} />
        </div>
        <div className="space-y-3">
          <PolicyToggle label="Enable Amendment Fee" checked={settings.cancellationPolicies.scheduled.enableAmendmentFee} onChange={checked => {
            const newSettings = {
              ...settings,
              cancellationPolicies: {
                ...settings.cancellationPolicies,
                scheduled: { ...settings.cancellationPolicies.scheduled, enableAmendmentFee: checked },
              },
            };
            setSettings(newSettings);
            setTimeout(() => saveSettings(newSettings), 1000);
          }} />
          <AdminField label="Amendment Fee (%)" value={settings.cancellationPolicies.scheduled.amendmentFeePercent.toString()} type="number" disabled={!settings.cancellationPolicies.scheduled.enableAmendmentFee} onChange={v => {
            const newSettings = {
              ...settings,
              cancellationPolicies: {
                ...settings.cancellationPolicies,
                scheduled: { ...settings.cancellationPolicies.scheduled, amendmentFeePercent: parseInt(v) },
              },
            };
            setSettings(newSettings);
            setTimeout(() => saveSettings(newSettings), 1000);
          }} />
        </div>
        <div className="space-y-3">
          <PolicyToggle label="Enable Name Change Fee" checked={settings.cancellationPolicies.scheduled.enableNameChangeFee} onChange={checked => {
            const newSettings = {
              ...settings,
              cancellationPolicies: {
                ...settings.cancellationPolicies,
                scheduled: { ...settings.cancellationPolicies.scheduled, enableNameChangeFee: checked },
              },
            };
            setSettings(newSettings);
            setTimeout(() => saveSettings(newSettings), 1000);
          }} />
          <AdminField label="Name Change Fee (%)" value={settings.cancellationPolicies.scheduled.nameChangeFeePercent.toString()} type="number" disabled={!settings.cancellationPolicies.scheduled.enableNameChangeFee} onChange={v => {
            const newSettings = {
              ...settings,
              cancellationPolicies: {
                ...settings.cancellationPolicies,
                scheduled: { ...settings.cancellationPolicies.scheduled, nameChangeFeePercent: parseInt(v) },
              },
            };
            setSettings(newSettings);
            setTimeout(() => saveSettings(newSettings), 1000);
          }} />
        </div>
      </div>
    </section>

  </div>
);
